import { useState, useEffect } from 'react';
import { useDate } from '../../contexts/date-context';
import { Button } from '../ui/button';
import { FormField, Label, Input } from '../ui/input';
import { CurrencyInput } from '../ui/currency-input';
import { Select } from '../ui/select-custom';
import { type CategoryType, categoryTypes, type Expense, type Tag } from '../../types';
import type { ComponentProps } from 'react';

function getDaysInMonth(month: number, year: number): number {
  return new Date(year, month, 0).getDate();
}

function getDayOptions(month: number, year: number) {
  return Array.from({ length: getDaysInMonth(month, year) }, (_, i) => ({
    value: (i + 1).toString(),
    label: (i + 1).toString(),
  }));
}

export interface ExpenseFormData {
  title: string;
  description: string;
  value: number;
  date: Date;
  categoryType: CategoryType;
  tags: string[];
}

export interface ExpenseFormProps
  extends Omit<ComponentProps<'form'>, 'onSubmit'> {
  onSubmit: (data: ExpenseFormData) => void;
  onClose?: () => void;
  initialData?: Expense | null;
  availableTags?: Tag[];
}

export function ExpenseForm({
  onSubmit,
  onClose,
  initialData,
  availableTags = [],
  ...props
}: ExpenseFormProps) {
  const { month, year } = useDate();

  const getInitialDay = () => {
    if (initialData?.date) {
      return new Date(initialData.date).getDate();
    }
    return new Date().getDate();
  };

  const [title, setTitle] = useState(initialData?.title || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [value, setValue] = useState(initialData?.value || 0);
  const [day, setDay] = useState(getInitialDay());
  const [categoryType, setCategoryType] = useState<CategoryType>(
    initialData?.categoryType || 'needs'
  );
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(
    initialData?.tags || []
  );

  const isEditing = !!initialData;

  // Reset form when initialData changes
  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title);
      setDescription(initialData.description || '');
      setValue(initialData.value);
      setDay(new Date(initialData.date).getDate());
      setCategoryType(initialData.categoryType);
      setSelectedTagIds(initialData.tags || []);
    } else {
      setTitle('');
      setDescription('');
      setValue(0);
      setDay(new Date().getDate());
      setCategoryType('needs');
      setSelectedTagIds([]);
    }
  }, [initialData]);

  const toggleTag = (tagId: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  // Validation
  const isFormValid =
    title.trim() !== '' && value > 0 && day > 0 && categoryType;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) {
      return;
    }

    // Create date from day, month, and year
    const date = new Date(year, month - 1, day);

    onSubmit({
      title,
      description,
      value,
      date,
      categoryType,
      tags: selectedTagIds,
    });

    if (!isEditing) {
      setTitle('');
      setDescription('');
      setValue(0);
      setDay(new Date().getDate());
      setCategoryType('needs');
      setSelectedTagIds([]);
    }
  };

  return (
    <form
      data-slot="expense-form"
      onSubmit={handleSubmit}
      className="flex flex-col gap-4"
      {...props}
    >
      <div className="grid grid-cols-12 gap-4">
        <FormField className="col-span-6">
          <Label htmlFor="title">
            Título <span className="text-destructive">*</span>
          </Label>
          <Input
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex: Compras do mês"
            required
          />
        </FormField>

        <FormField className="col-span-4">
          <Label htmlFor="value">
            Valor <span className="text-destructive">*</span>
          </Label>
          <CurrencyInput
            id="value"
            value={value}
            onChange={setValue}
            required
          />
        </FormField>

        <FormField className="col-span-2">
          <Label htmlFor="day">
            Dia <span className="text-destructive">*</span>
          </Label>
          <Select
            value={day.toString()}
            onChange={(value) => setDay(parseInt(value))}
            options={getDayOptions(month, year)}
          />
        </FormField>
      </div>

      <FormField>
        <Label>
          Categoria <span className="text-destructive">*</span>
        </Label>
        <div className="grid grid-cols-2 gap-2">
          {Object.values(categoryTypes).filter((c) => c.name === 'needs' || c.name === 'comfort').map((category) => (
            <label
              key={category.name}
              className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 transition-colors ${
                categoryType === category.name
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:bg-muted'
              }`}
            >
              <input
                type="radio"
                name="categoryType"
                value={category.name}
                checked={categoryType === category.name}
                onChange={(e) =>
                  setCategoryType(e.target.value as CategoryType)
                }
                className="sr-only"
              />
              <span
                className="size-3 shrink-0 rounded-full"
                style={{ backgroundColor: category.color }}
              />
              <span className="text-sm text-foreground">{category.label}</span>
            </label>
          ))}
        </div>
      </FormField>

      {availableTags.length > 0 && (
        <FormField>
          <Label>Tags</Label>
          <div className="flex flex-wrap gap-2">
            {availableTags.map((tag) => {
              const selected = selectedTagIds.includes(tag.id);
              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => toggleTag(tag.id)}
                  className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                    selected
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-foreground hover:bg-muted'
                  }`}
                >
                  <span>{tag.icon}</span>
                  <span>{tag.name}</span>
                </button>
              );
            })}
          </div>
        </FormField>
      )}

      <FormField>
        <Label htmlFor="description">Descrição</Label>
        <Input
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Detalhes sobre o gasto (opcional)"
        />
      </FormField>

      <div className="flex gap-2">
        {onClose && (
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={onClose}
          >
            Cancelar
          </Button>
        )}
        <Button type="submit" className="flex-1" disabled={!isFormValid}>
          {isEditing ? 'Salvar alterações' : 'Adicionar gasto'}
        </Button>
      </div>
    </form>
  );
}
